import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Send } from "lucide-react";
import "./inbox.css";

// Staff Unified Inbox: conversation threads over /api/inbox/*.
// `api` is App.js's axios instance (baseURL already ends in /api).

const CHANNEL_LABELS = {
  sms: "SMS",
  whatsapp: "WhatsApp",
  email: "Email",
  website: "Website",
  portal: "Client Portal",
  instagram: "Instagram",
  facebook: "Facebook",
  other: "Other",
};

const STATUS_LABELS = {
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
  suppressed: "Blocked (opted out)",
  queued: "Queued",
};

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return "Please check the message and try again.";
  return fallback;
};

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

function ChannelStrip({ channels }) {
  if (!channels.length) return null;
  return (
    <section className="inbox-channel-strip glass-panel" data-testid="inbox-channel-status">
      {channels.map((c) => (
        <span key={c.key} className={`inbox-chip ${c.status}`} title={c.note || ""} data-testid={`inbox-channel-${c.key}`}>
          {c.label}: {c.status === "connected" ? "Connected" : c.status === "coming_soon" ? "Coming soon · not connected" : "Not configured"}
        </span>
      ))}
    </section>
  );
}

function ThreadList({ threads, selectedId, onSelect, loading }) {
  return (
    <div className="panel glass-panel inbox-thread-list" data-testid="inbox-thread-list">
      {loading && !threads.length && <div className="empty-cell">Loading conversations…</div>}
      {!loading && !threads.length && <div className="empty-cell">No conversations match these filters.</div>}
      {threads.map((t) => (
        <button
          key={t.thread_id}
          type="button"
          className={`inbox-thread-item ${t.thread_id === selectedId ? "active" : ""} ${t.unread_count ? "unread" : ""}`}
          onClick={() => onSelect(t.thread_id)}
          data-testid={`inbox-thread-${t.thread_id}`}
        >
          <div className="inbox-thread-top">
            <strong>{t.contact_name || t.clinic_name || "Unknown contact"}</strong>
            {t.unread_count > 0 && <span className="inbox-badge unread">{t.unread_count}</span>}
          </div>
          <p>{t.last_message?.direction === "outbound" ? "You: " : ""}{t.last_message?.body}</p>
          <div className="inbox-meta">
            <span className="inbox-badge">{t.channel_label}</span>
            {!t.channel_connected && <span className="inbox-badge">not connected</span>}
            {t.thread_status === "closed" && <span className="inbox-badge">closed</span>}
            {t.assigned_to_name && <span>→ {t.assigned_to_name}</span>}
            <span>{formatWhen(t.last_at)}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function ReplyBox({ thread, onSent, api, showNotice }) {
  const replyChannels = thread.reply_channels || [];
  // Remounted per thread (key=thread_id), so initial state is per thread.
  const [channel, setChannel] = useState(thread.default_reply_channel || replyChannels[0] || "");
  const [subject, setSubject] = useState(thread.subject ? `Re: ${thread.subject}` : "");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const notConnected = ["instagram", "facebook"].includes(thread.channel_key);
  if (!replyChannels.length) {
    return (
      <div className="inbox-reply" data-testid="inbox-reply-unavailable">
        <span className="inbox-note">
          {notConnected
            ? `${CHANNEL_LABELS[thread.channel_key]} is not connected yet — replying to DMs is coming soon. No phone or email is on file for this contact.`
            : "No phone number, email address or portal account is on file for this contact, so there is no way to reply from here."}
        </span>
      </div>
    );
  }

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/inbox/threads/${thread.thread_id}/reply`, {
        body,
        channel,
        subject: channel === "email" ? subject || undefined : undefined,
      });
      const message = res.data?.message || {};
      if (res.data?.delivered) {
        showNotice(`Reply ${message.status === "delivered" ? "posted to the client portal" : `sent via ${CHANNEL_LABELS[channel]}`}`, "success");
        setBody("");
      } else {
        showNotice(`Not sent: ${message.failure_reason || STATUS_LABELS[message.status] || message.status}`, "error");
      }
      await onSent();
    } catch (err) {
      showNotice(errorText(err, "Reply failed"), "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="inbox-reply" data-testid="inbox-reply-box">
      <div className="inbox-reply-row">
        <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="inbox-reply-channel">
          {replyChannels.map((c) => <option key={c} value={c}>{CHANNEL_LABELS[c] || c}</option>)}
          <option value="instagram" disabled>Instagram (coming soon)</option>
          <option value="facebook" disabled>Facebook (coming soon)</option>
        </select>
        {channel === "email" && (
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" maxLength={200} data-testid="inbox-reply-subject" />
        )}
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={5000}
        placeholder={channel === "portal" ? "Message the clinic in their portal…" : `Reply via ${CHANNEL_LABELS[channel] || channel}…`}
        data-testid="inbox-reply-body"
      />
      <div className="inbox-reply-row" style={{ justifyContent: "space-between" }}>
        <span className="inbox-note">Opted-out recipients are blocked automatically. Failed sends are kept in the thread with the reason.</span>
        <button className="primary-btn" type="button" onClick={send} disabled={sending || !body.trim()} data-testid="inbox-reply-send">
          <Send size={15} /> {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}

function ThreadView({ api, threadId, team, user, showNotice, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (markRead = false) => {
    if (!threadId) return;
    setLoading(true);
    try {
      const res = await api.get(`/inbox/threads/${threadId}`);
      setData(res.data);
      if (markRead && res.data?.thread?.unread_count) {
        await api.post(`/inbox/threads/${threadId}/read`, { read: true });
        onChanged();
      }
    } catch (err) {
      showNotice(errorText(err, "Could not load conversation"), "error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, threadId, showNotice, onChanged]);

  // Opening a conversation marks it read; later reloads (e.g. after
  // "Mark unread") must not.
  useEffect(() => { load(true); }, [load]);

  if (!threadId) return <div className="panel glass-panel empty-cell">Select a conversation.</div>;
  if (!data) return <div className="panel glass-panel empty-cell">{loading ? "Loading…" : "Conversation unavailable."}</div>;

  const { thread, messages } = data;
  const isRep = user?.role === "Sales Representative";
  const assignees = isRep ? team.filter((m) => m.id === user.id) : team.filter((m) => m.active !== false);

  const act = async (path, payload, done) => {
    try {
      await api.post(`/inbox/threads/${thread.thread_id}/${path}`, payload);
      showNotice(done, "success");
      await load();
      onChanged();
    } catch (err) {
      showNotice(errorText(err, "Update failed"), "error");
    }
  };

  return (
    <div className="panel glass-panel inbox-thread-view" data-testid="inbox-thread-view">
      <div className="inbox-thread-header">
        <div>
          <h3>{thread.contact_name || thread.clinic_name || "Unknown contact"}</h3>
          <div className="inbox-meta">
            <span className="inbox-badge">{thread.channel_label}</span>
            {thread.clinic_name && thread.clinic_name !== thread.contact_name && <span>{thread.clinic_name}</span>}
            {thread.contact_type && <span>{thread.contact_type}</span>}
            <span>{thread.thread_status === "closed" ? "Closed" : "Open"}</span>
          </div>
        </div>
        <div className="inbox-thread-actions">
          <select
            value={thread.assigned_to_id || ""}
            onChange={(e) => act("assign", { assignee_id: e.target.value || null }, "Assignment updated")}
            data-testid="inbox-assign-select"
          >
            <option value="">Unassigned</option>
            {thread.assigned_to_id && !assignees.some((m) => m.id === thread.assigned_to_id) && (
              <option value={thread.assigned_to_id}>{thread.assigned_to_name || "Assigned"}</option>
            )}
            {assignees.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button className="ghost-btn small" type="button" onClick={() => act("read", { read: false }, "Marked unread")} data-testid="inbox-mark-unread">
            Mark unread
          </button>
          {thread.thread_status === "closed" ? (
            <button className="ghost-btn small" type="button" onClick={() => act("status", { status: "open" }, "Conversation reopened")} data-testid="inbox-reopen">
              Reopen
            </button>
          ) : (
            <button className="ghost-btn small" type="button" onClick={() => act("status", { status: "closed" }, "Conversation closed")} data-testid="inbox-close">
              Close
            </button>
          )}
        </div>
      </div>

      <div className="inbox-messages" data-testid="inbox-messages">
        {messages.map((m) => {
          const outbound = m.direction === "outbound";
          const via = m.delivery_channel || m.channel_key;
          return (
            <div key={m.id} className={`inbox-bubble ${outbound ? "outbound" : "inbound"}`}>
              {m.subject && !outbound && <strong style={{ display: "block", marginBottom: 4 }}>{m.subject}</strong>}
              {m.body}
              <div className="inbox-meta">
                <span>{outbound ? (m.author_name || "Team") : (m.author_name || m.from_name || thread.contact_name || "Contact")}</span>
                {via && <span className="inbox-badge">{CHANNEL_LABELS[via] || via}</span>}
                {outbound && m.status && <span className={`inbox-badge ${m.status}`}>{STATUS_LABELS[m.status] || m.status}</span>}
                <span>{formatWhen(m.created_at)}</span>
              </div>
              {m.failure_reason && <div className="inbox-failure">{m.failure_reason}</div>}
            </div>
          );
        })}
      </div>

      <ReplyBox key={thread.thread_id} thread={thread} api={api} showNotice={showNotice} onSent={async () => { await load(); onChanged(); }} />
    </div>
  );
}

export default function UnifiedInbox({ api, team = [], user, showNotice }) {
  const [threads, setThreads] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: "open", channel: "", assigned: "", unread: false });
  const [loading, setLoading] = useState(false);
  // App's showNotice is a new function every render; keep callbacks stable
  // so a notice never re-triggers the loads that raised it.
  const noticeRef = useRef(showNotice);
  noticeRef.current = showNotice;
  const notify = useCallback((message, type) => noticeRef.current?.(message, type), []);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: filters.status };
      if (filters.channel) params.channel = filters.channel;
      if (filters.assigned) params.assigned = filters.assigned;
      if (filters.unread) params.unread = true;
      const res = await api.get("/inbox/threads", { params });
      setThreads(res.data?.threads || []);
    } catch (err) {
      notify(errorText(err, "Could not load the inbox"), "error");
    } finally {
      setLoading(false);
    }
  }, [api, filters, notify]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    api.get("/inbox/channels").then((res) => setChannels(res.data?.channels || [])).catch(() => setChannels([]));
  }, [api]);

  const unreadTotal = threads.reduce((sum, t) => sum + (t.unread_count || 0), 0);
  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="page-stack" data-testid="inbox-center">
      <section className="module-toolbar glass-panel">
        <div>
          <p className="eyebrow">Unified Inbox • {threads.length} conversations • {unreadTotal} unread</p>
          <h2>Unified Communication Inbox</h2>
          <span>SMS, WhatsApp, website forms and client-portal messages. Reply by SMS, WhatsApp, email or portal.</span>
        </div>
        <div className="inbox-filters">
          <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} data-testid="inbox-filter-status">
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
          <select value={filters.channel} onChange={(e) => setFilter("channel", e.target.value)} data-testid="inbox-filter-channel">
            <option value="">All channels</option>
            {["sms", "whatsapp", "email", "website", "portal", "instagram", "facebook"].map((c) => (
              <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>
            ))}
          </select>
          <select value={filters.assigned} onChange={(e) => setFilter("assigned", e.target.value)} data-testid="inbox-filter-assigned">
            <option value="">Anyone</option>
            <option value="me">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
          </select>
          <label>
            <input type="checkbox" checked={filters.unread} onChange={(e) => setFilter("unread", e.target.checked)} data-testid="inbox-filter-unread" />
            Unread only
          </label>
          <button className="ghost-btn small" type="button" onClick={loadThreads} data-testid="inbox-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </section>

      <ChannelStrip channels={channels} />

      <div className="inbox-layout">
        <ThreadList threads={threads} selectedId={selected} onSelect={setSelected} loading={loading} />
        <ThreadView api={api} threadId={selected} team={team} user={user} showNotice={notify} onChanged={loadThreads} />
      </div>
    </div>
  );
}
